'use client';

import React, { useRef } from 'react';

import { RoleBasedToggle, UnitSystem } from '@ravago/shared/page-data/models/config';
import { BasicFacetWithTermsV2, ElasticProduct, OverviewPageItem } from '@ravago/shared/page-data/models/elastic';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';

import { ProductTable } from '../overview/product-table';

import { ProductPortfolioPagination } from './product-portfolio-pagination';

interface Props {
   baseRoutes?: {
      brand: string;
      caseStudy: string;
      product: string;
      market: string;
      technology: string;
   };
   globals: RendererGlobals;
   onPageChange: (page: number) => void;
   page: number;
   hasPublicLabelAndSigningsToggles: boolean;
   hasSustainabilityLabels: boolean;
   allowShowPhyiscalProperties: boolean;
   content: Array<OverviewPageItem>;
   totalPages?: number;
   unitSystem: UnitSystem;
   activeColumnsUpdate: (newActiveColumns: string[]) => void;
   activeColumns?: string[];
   activeFacets?: BasicFacetWithTermsV2[];
   roleBasedToggles: RoleBasedToggle;
}

export const ProductPortfolioTable: React.FC<Props> = ({
   baseRoutes,
   content,
   globals,
   onPageChange,
   page,
   hasPublicLabelAndSigningsToggles,
   hasSustainabilityLabels,
   totalPages = 1,
   allowShowPhyiscalProperties,
   unitSystem,
   activeFacets,
   activeColumns,
   activeColumnsUpdate,
   roleBasedToggles
}) => {
   const containerRef = useRef<HTMLDivElement>(null);

   const handlePageChange = (page: number) => {
      onPageChange(page);
      if (!containerRef.current) return;

      window.scrollTo({ top: containerRef.current.offsetTop, behavior: 'smooth' });
   };

   return (
      <div ref={containerRef}>
         <Box direction="column">
            {content[0].qualifier === 'product' && (
               <ProductTable
                  data-testid="product-table-view"
                  globals={globals}
                  activeFacets={activeFacets}
                  baseRoutes={baseRoutes}
                  hasPublicLabelAndSigningsPermission={hasPublicLabelAndSigningsToggles}
                  hasSustainabilityLabels={hasSustainabilityLabels}
                  allowShowPhyiscalProperties={allowShowPhyiscalProperties}
                  unitSystem={unitSystem}
                  products={content as Array<ElasticProduct>}
                  activeColumnsUpdate={activeColumnsUpdate}
                  activeColumnKeys={activeColumns}
                  roleBasedToggles={roleBasedToggles}
               />
            )}
            <Box justify="end">
               <ProductPortfolioPagination current={page} total={totalPages} onChange={handlePageChange} />
            </Box>
         </Box>
      </div>
   );
};
